<template>
  <Loader v-if="loader" />
  <h2 class="ml-3">Riepilogo Gettoni</h2>
  <div class="card">
    <div class="formgrid grid">
      <div class="field col-12 md:col-3">
        <label>Mese</label>
        <Calendar
          v-model="selectedDate"
          :showIcon="true"
          view="month"
          dateFormat="MM yy"
          :class="[
            'inputfield w-full',
            { 'p-invalid': validationErrors.date && submitted },
          ]"
        />
        <div v-show="validationErrors.date && submitted" class="p-error">
          Seleziona data.
        </div>
      </div>
      <div class="field col-12 md:col-3">
        <label>Agente</label>
        <Dropdown
          v-model="selectedAgent"
          :options="agents"
          optionLabel="name"
          optionValue="id"
          dataKey="id"
          :filter="true"
          placeholder="Seleziona Agente"
          class="w-full"
        >
          <template #option="slotProps">
            {{ slotProps.option.name }} ({{ slotProps.option.gettone_periodo }})
          </template>
          <template #value="slotProps">
            {{ slotProps.value ? `${getSelectedAgentName(slotProps.value)} (${getSelectedAgentGettone(slotProps.value)})` : 'Seleziona Agente' }}
          </template>
        </Dropdown>
      </div>
      <div class="field col-12 md:col-3">
        <label for="codice-importazione">Codice importazione</label>
        <InputText
          id="codice-importazione"
          v-model="codiceImportazione"
          class="w-full"
        />
      </div>
      <div
        class="field col-12 md:col-2 flex align-items-end justify-content-center"
      >
        <Button
          label="Cerca"
          icon="pi pi-search"
          iconPos="left"
          class="w-full"
          @click="searchData"
        />
      </div>
    </div>
  </div>

  <div class="card" v-if="loaded">
    <div class="flex justify-content-end mb-4">
      <Button  v-if="items.length"
        label="Esporta CSV" 
        icon="pi pi-download" 
        @click="exportCSV" 
        class="p-button-secondary"
      />
    </div>
    <div class="legend mb-4">
      <div class="legend-item">
        <div class="legend-color" style="background-color: #fee2e2"></div>
        <span style="font-size: 18px">Storno</span>
      </div>
      <div class="legend-item">
        <div class="legend-color" style="background-color: #fef9c3"></div>
        <span style="font-size: 18px"> Rinnovo</span>
      </div>
      <div class="legend-item">
        <div class="legend-color" style="background-color: #bfd6e9"></div>
        <span style="font-size: 18px"> Gettone non associato all'offerta</span>
      </div>
    </div>

    <div class="field mb-4">
      <span class="p-input-icon-left w-full">
        <i class="pi pi-search" />
        <InputText
          v-model="searchQuery"
          placeholder="Cerca per POD o PDR"
          class="w-full"
        />
      </span>
    </div>

    <DataTable 
      :value="filteredItems"
      responsiveLayout="scroll"
      :paginator="true"
      :rows="20"
      tableStyle="min-width: 50rem"
      :rowStyle="
        (data) => ({
          backgroundColor: getBackgroundRow(data.tipo)
        })
      "
      :sortField="'datetime'"
      :sortOrder="-1"
    >
      <template #empty>Nessun risultato trovato</template>
      <Column field="id" header="ID" sortable></Column>
      <Column field="code_contratto" header="Codice contratto" sortable></Column>
      <!-- <Column field="stato_caricamento" header="Stato Caricamento" sortable></Column> -->
      <Column field="agente_name" header="Agente" sortable></Column>
      <Column field="intestazione_agente" header="Intestazione Agente" sortable></Column>
      <Column field="referente_agente" header="Referente Agente" sortable></Column>
      <Column field="partita_iva_agente" header="Partita Iva Agente" sortable></Column>
      <Column field="denominazione_cliente" header="Denominazione Cliente" sortable></Column>
      <Column field="type_client" header="Tipo Cliente" sortable></Column>
      <Column field="cf_cliente" header="Codice Fiscale" sortable></Column>
      <Column field="code_offerta" header="Offerta" sortable></Column>
      <Column field="tipo_fornitura" header="Tipo Fornitura" sortable></Column>
      <Column field="pdp" header="PDP" sortable></Column>
      <Column field="data_inizio_pdp" header="Data inizio PDP" sortable>
        <template #body="{data}">
          {{ data.data_inizio_pdp ? moment(data.data_inizio_pdp).format('DD/MM/YYYY') : '' }}
        </template>
      </Column>
      <Column field="totale" header="Totale" sortable>
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.totale) }}
        </template>
      </Column>
      <!-- <Column field="totale_agente" header="Totale agente" sortable>
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.totale_agente) }}
        </template>
      </Column> -->
      <Column field="mese_da" header="Competenza" sortable>
        <template #body="slotProps">
          <span style="white-space: nowrap;">{{ slotProps.data.mese_da }} - {{ slotProps.data.mese_a }}</span>
        </template>
      </Column>
      <Column field="note" header="Note"></Column>
      <Column field="gettone_riferimento" header="Totale gettone di riferimento">
        <template #body="{data}">
          {{ formatNullableCurrency(data.gettone_riferimento) }}
        </template>      
      </Column>

      <Column field="mese_gettone_riferimento" header="Mese gettone di riferimento">
        <template #body="{data}">
          {{ formatDateIfValid(data.mese_gettone_riferimento) }}
        </template>
      </Column>
      <ColumnGroup type="footer">
        <Row>
          <Column :colspan="13" footer="Totali:"/>
          <Column :footer="getTotals('totale')"/>
          <!-- <Column :footer="getTotals('totale_agente')"/> -->
          <Column :colspan="4"/>
        </Row>
      </ColumnGroup>
    </DataTable>
  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance, computed } from "vue";
import moment from "moment";
import Service from "@/services/ask-pratiche/pratiche-service";
import UtilityService from "@/services/ask-management/utility-service";
import Loader from "@/components/Loader.vue";

export default {
  components: {
    Loader,
  },
  setup() {
    const loader = ref(false);
    const items = ref([]);
    const agents = ref([]);
    const selectedAgent = ref(null);
    const selectedDate = ref(null);
    const codiceImportazione = ref("");
    const validationErrors = ref({});
    const submitted = ref(false);
    const searchQuery = ref("");

    const loaded = ref(false)
    const self = getCurrentInstance();
    self.$http = self.appContext.config.globalProperties.$http;
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;

    const service = new Service(self.$http, self.$eventBus);
    const utilityService = new UtilityService(self.$http, self.$eventBus);

    onMounted(() => {
      getAgentiPerGettoni();
    });

    const getAgentiPerGettoni = () => {
      loader.value = true;
      utilityService
        .getAgentiPerGettoni(1)
        .then((res) => {
          if (res.success) {
            agents.value = res.items;
          } else {
            console.error("Nessun agente trovato o errore nel caricamento");
            self.$eventBus.emit("data-load-error", {
              message: "Nessun agente trovato o errore nel caricamento",
            });
          }
        })
        .catch((error) => {
          console.error("Errore nel recupero degli agenti:", error);
          self.$eventBus.emit("data-load-error", {
            message: "Errore nel recupero degli agenti",
          });
        })
        .finally(() => {
          loader.value = false;
        });
    };

    const searchData = async () => {
      submitted.value = true;
      loaded.value = false
      if (validateData()) {
        loader.value = true;
        try {
          const response = await service.getRiepilogoGettoni(
            selectedAgent.value,
            moment(selectedDate.value).format("YYYY-MM"),
            codiceImportazione.value
          );
          items.value = response.items;
          loaded.value = true
        } catch (error) {
          console.error("Errore nel recupero dei dati:", error);
        } finally {
          loader.value = false;
        }
      }
    };

    const validateData = () => {
      if (!selectedDate.value) {
        validationErrors.value["date"] = true;
      } else {
        delete validationErrors.value["date"];
      }
      return !Object.keys(validationErrors.value).length;
    };

    const formatCurrency = (value) => {
      return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
      }).format(value);
    };

    const formatNullableCurrency = (value) => {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const numericValue = parseFloat(value);
      return Number.isNaN(numericValue) ? null : formatCurrency(numericValue);
    };

    const formatDate = (date) => {
      return moment(date).format("DD/MM/YYYY");
    };

    const formatDateIfValid = (date) => {
      if (!date) return "";

      const parsedDate = moment(date);
      return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY") : "";
    };

    const formatDateTime = (datetime) => {
      return moment(datetime).format("DD/MM/YYYY HH:mm");
    };

    const getTipoLabel = (tipo) => {
      let label = {
        1: 'Acconto',
        2: 'Storno',
        3: 'Rinnovo'
      }
      return label[tipo]
      // const row = document.querySelector(`tr[data-row-id="${tipo}"]`);
      // if (row) {
      //   row.setAttribute("data-tipo", tipo);
      // }
      // return tipo === 2 ? "Storno" : "Acconto";
    };

    const getBackgroundRow = (tipo) => {
      let bg = {
        1: '', 
        2: '#fee2e2', 
        3: '#fef9c3',
        10: '#bfd6e9'
      }
      return bg[tipo] || '';
    }

    const getTerminatoLabel = (terminato) => {
      return terminato ? "Completato" : "In corso";
    };

    const getTerminatoClass = (terminato) => {
      return {
        "status-badge": true,
        "status-success": terminato,
        "status-warning": !terminato,
      };
    };

    const getDataFineClass = (data) => {
      if (!data) return "";

      const dataFine = moment(data);
      const oggi = moment();

      return {
        "text-red-500": dataFine.isBefore(oggi, "day"),
        "text-green-500": dataFine.isAfter(oggi, "day"),
      };
    };

    const filteredItems = computed(() => {
      if (!searchQuery.value) return items.value;

      const query = searchQuery.value.toLowerCase();
      return items.value.filter((item) => {
        const pod = (item.pod || "").toLowerCase();
        const pdr = (item.pdr || "").toLowerCase();
        return pod.includes(query) || pdr.includes(query);
      });
    });
    const getTotals = (tipo_dato, formatted = true) => {
      let total = filteredItems.value.reduce((a, b) => {
        const valore = parseFloat(b[tipo_dato]);        
        return a + (isNaN(valore) ? 0 : valore);
      }, 0);
      return formatted ? formatCurrency(total) : total;
    }

    const formatCsvDecimalWithComma = (value) => {
      const numericValue = parseFloat(value);
      if (Number.isNaN(numericValue)) return '';

      return numericValue.toFixed(2).replace('.', ',');
    };

    const isStorno = (data) => {
      const isStornoRow = data.tipo === 2;
      return isStornoRow;
    };

    const getSelectedAgentName = (id) => {
      const agent = agents.value.find(a => a.id === id);
      return agent ? agent.name : '';
    };

    const getSelectedAgentGettone = (id) => {
      const agent = agents.value.find(a => a.id === id);
      return agent ? agent.gettone_periodo : '';
    };

    const formatMonthYear = (date) => {
      return moment(date).locale('it').format('MMMM YYYY');
    };

    const formatScaglione = (min, max) => {
      if (min === null && max === null) return '-';
      if (min === null) return `fino a ${max}`;
      if (max === null) return `da ${min}`;
      return `da ${min} a ${max}`;
    };

    const exportCSV = () => {
      const agentName = getSelectedAgentName(selectedAgent.value)
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');

      const csvData = filteredItems.value.map(item => ({
        'ID': item.id,
        'Codice contratto': item.code_contratto,
        // 'Stato Caricamento': item.stato_caricamento,
        'Agente': item.agente_name,
        'Intestazione': item.intestazione_agente,
        'Referente': item.referente_agente,
        'Partita iva': item.partita_iva_agente,
        'Denominazione Cliente': item.denominazione_cliente,
        'Tipo Cliente': item.type_client,
        'Codice Fiscale': item.cf_cliente,
        'Offerta': item.code_offerta,
        'Tipo Fornitura': item.tipo_fornitura,
        'PDP': item.pdp,
        'Data inizio': item.data_inizio_pdp ? moment(item.data_inizio_pdp).format('DD/MM/YYYY') : '',
        'Totale': formatCsvDecimalWithComma(item.totale),
        // 'Totale agente': item.totale_agente,
        'Competenza': `${item.mese_da} - ${item.mese_a}`,
        'Note': item.note,
        'Totale gettone riferimento': formatNullableCurrency(item.gettone_riferimento),
        'Mese gettone riferimento': formatDateIfValid(item.mese_gettone_riferimento)
      }));

      const headers = Object.keys(csvData[0]);
      
      const totalRow = headers.reduce((acc, header) => {
        if (header === 'Totale') {
          acc[header] = formatCsvDecimalWithComma(getTotals('totale', false));
        // } else if (header === 'Totale agente') {
        //   acc[header] = getTotals('totale_agente', false);
        } else if (header === 'ID') {
          acc[header] = 'Totali:';
        } else {
          acc[header] = '';
        }
        return acc;
      }, {});

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => 
          headers.map(header => {
            let cell = row[header]?.toString() || '';
            if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
              cell = `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
          }).join(',')
        ),
        headers.map(header => {
          let cell = totalRow[header]?.toString() || '';
          if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
            cell = `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        }).join(',')
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `riepilogo_gettoni_${agentName}_${moment().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return {
      moment,
      loader,
      items,
      agents,
      selectedAgent,
      selectedDate,
      codiceImportazione,
      validationErrors,
      submitted,
      searchData,
      formatCurrency,
      formatNullableCurrency,
      formatDate,
      formatDateIfValid,
      formatDateTime,
      getTipoLabel,
      getTerminatoLabel,
      getTerminatoClass,
      getDataFineClass,
      searchQuery,
      filteredItems,
      isStorno,
      getSelectedAgentName,
      getSelectedAgentGettone,
      formatMonthYear,
      formatScaglione,
      exportCSV,
      loaded,
      getBackgroundRow,
      getTotals
    };
  },
};
</script>

<style scoped>
.riepilogo-provvigioni .p-datatable .p-datatable-thead > tr > th.th-power {
  background: rgba(251, 230, 138, 0.742) !important;
  min-width: 120px;
}

.riepilogo-provvigioni .p-datatable .p-datatable-thead > tr > th.th-gas {
  background: rgba(137, 185, 244, 0.412) !important;
  min-width: 120px;
}

.riepilogo-provvigioni .p-datatable .p-datatable-tbody > tr > td.border-lt,
.p-datatable .p-datatable-thead > tr > th.border-lt {
  border-left: 1px solid rgba(62, 62, 62, 0.093);
}
.riepilogo-provvigioni .p-datatable .p-datatable-tfoot > tr > td.td-footer {
  color: #0a8cf5 !important;
  font-size: 15px;
}
.riepilogo-provvigioni .p-datatable .p-datatable-tbody > tr > td.sticky-cell {
  position: sticky !important;
  left: 0 !important;
  background-color: rgb(249, 249, 249);
}

:deep(.p-datatable .p-datatable-tbody > tr) {
  background-color: inherit;
}

:deep(.p-datatable .p-datatable-tbody > tr[data-tipo="2"]) {
  background-color: #fee2e2 !important;
}

.storno-row {
  color: red !important;
}

:deep(.storno-row) {
  color: red !important;
}

:deep(.p-datatable .p-datatable-tbody > tr.storno-row) {
  color: red !important;
}
</style>
<style scoped>
.btn-year {
  min-width: 100px;
}
.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 700;
}
.status-success {
  background-color: #c8e6c9;
  color: #256029;
}
.status-warning {
  background-color: #feedaf;
  color: #8a5340;
}

.p-input-icon-left {
  width: 100%;
}

.p-input-icon-left i {
  left: 0.75rem;
  color: var(--text-color-secondary);
}

.p-input-icon-left input {
  padding-left: 2.5rem;
}

.w-4 {
  width: 1rem;
}

.h-4 {
  height: 1rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.gap-4 {
  gap: 1rem;
}

.w-4.h-4 {
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.legend {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 1rem;
  height: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}
</style>

